import React, { useState } from "react";

import { getPpeIcon, getPpeIconInfo } from "../../utils/eventUtils";

/**
 * 안전장구 미착용 '확인(결재)' 모달.
 * 미확인 위반 목록을 보여주고, 현장 관리자 이름 + 조치 내용을 입력해
 * 각 건을 확인하면 위험에서 내려간다. (사람이 확인해야만 해제)
 */
export default function PpeAckModal({
  open,
  onClose,
  items,
  total,
  onAck,
  onFalsePositive,
  submittingId,
  ackByName = "",
}) {
  const [notes, setNotes] = useState({});

  if (!open) return null;

  const list = Array.isArray(items) ? items : [];
  // total 은 서버가 센 <전체> 미확인 건수(상한 없음). 안 오면 목록 길이로 대체.
  const totalCount = Number.isFinite(Number(total)) ? Number(total) : list.length;
  // 확인자 이름 = 로그인 계정 이름(자동). 사용자는 사유(조치내용)만 적으면 됨.
  const who = String(ackByName || "").trim();
  const managerOk = who.length > 0;

  return (
    <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-slate-900/55 p-4 backdrop-blur-sm">
      <div className="flex max-h-[86vh] w-full max-w-xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/5 dark:bg-[#0F172A] dark:ring-white/10">
        {/* 헤더 */}
        <div className="flex items-start gap-3 border-b border-gray-100 bg-amber-50 px-6 py-5 dark:border-white/10 dark:bg-amber-500/10">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-amber-100 text-2xl dark:bg-amber-500/20">
            ⚠️
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-lg font-bold text-gray-900 dark:text-white">
              안전장구 미착용 확인
            </div>
            <div className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
              미확인{" "}
              <span className="font-semibold text-amber-600 dark:text-amber-400">
                {totalCount}건
              </span>
              {/* 목록은 최근 100건까지만 내려온다. 잘렸으면 그 사실을 숨기지 않는다. */}
              {totalCount > list.length ? ` (최근 ${list.length}건 표시)` : ""}
              {" · "}현장 관리자가 확인해야 처리됩니다.
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="-mr-1 -mt-1 rounded-full p-1.5 text-gray-400 transition hover:bg-black/5 hover:text-gray-700 dark:hover:bg-white/10 dark:hover:text-white"
            aria-label="닫기"
          >
            ✕
          </button>
        </div>

        {/* 확인자 = 로그인 계정 이름(자동). 입력 불필요, 사유만 적으면 됨. */}
        <div className="border-b border-gray-100 px-6 py-4 dark:border-white/10">
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            확인자
          </label>
          <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-100 px-4 py-2.5 text-sm font-semibold text-gray-800 dark:border-white/10 dark:bg-[#0B1220] dark:text-white">
            <span>👤</span>
            <span>{who || "(로그인 정보 없음)"}</span>
          </div>
        </div>

        {/* 위반 목록 */}
        <div className="min-h-0 flex-1 space-y-2.5 overflow-y-auto bg-gray-50/60 px-6 py-4 dark:bg-transparent">
          {list.length === 0 ? (
            <div className="py-12 text-center text-sm text-gray-400">
              미확인 위반이 없습니다.
            </div>
          ) : (
            list.map((v) => (
              <div
                key={v.id}
                className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/10 dark:bg-[#111827]"
              >
                <div className="flex items-center gap-3 border-l-4 border-amber-400 px-4 py-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gray-50 dark:bg-white/5">
                    {(() => {
                      const info = getPpeIconInfo({
                        eventReason: v.violationType,
                        type: v.violationLabel,
                      });
                      return info ? (
                        <span
                          aria-hidden
                          style={{
                            width: 22,
                            height: 22,
                            display: "inline-block",
                            backgroundColor: info.color,
                            WebkitMaskImage: `url(${info.src})`,
                            maskImage: `url(${info.src})`,
                            WebkitMaskSize: "contain",
                            maskSize: "contain",
                            WebkitMaskRepeat: "no-repeat",
                            maskRepeat: "no-repeat",
                            WebkitMaskPosition: "center",
                            maskPosition: "center",
                          }}
                        />
                      ) : (
                        <span className="text-xl">
                          {getPpeIcon({
                            eventReason: v.violationType,
                            type: v.violationLabel,
                          })}
                        </span>
                      );
                    })()}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-bold text-gray-900 dark:text-white">
                      {v.deviceName || v.deviceId}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {v.violationLabel || v.violationType} · {v.createdAt}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 px-4 pb-3">
                  <input
                    type="text"
                    value={notes[v.id] || ""}
                    onChange={(e) =>
                      setNotes((p) => ({ ...p, [v.id]: e.target.value }))
                    }
                    onKeyDown={(e) => {
                      // 엔터로 바로 확인 처리(확인 버튼 클릭과 동일). 확인 버튼도 그대로 유지.
                      // 한글 IME 조합중 엔터는 무시(조합확정용) — 중복 제출 방지.
                      if (
                        e.key === "Enter" &&
                        !e.nativeEvent.isComposing &&
                        managerOk &&
                        submittingId !== v.id
                      ) {
                        e.preventDefault();
                        onAck(v.id, who, notes[v.id] || "");
                      }
                    }}
                    placeholder="조치 내용 입력 후 Enter (또는 확인 버튼)"
                    className="min-w-0 flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none transition focus:border-amber-400 dark:border-white/10 dark:bg-[#0B1220] dark:text-white"
                  />
                  <button
                    type="button"
                    disabled={submittingId === v.id}
                    onClick={() => onFalsePositive?.(v.id)}
                    title="오탐(오인식)으로 표시 — 위험에서 내려가고 재학습에 반영됩니다"
                    className="shrink-0 rounded-lg border border-rose-300 bg-white px-3 py-2 text-sm font-bold text-rose-600 shadow-sm transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-rose-500/40 dark:bg-transparent dark:text-rose-300 dark:hover:bg-rose-500/10"
                  >
                    오탐
                  </button>
                  <button
                    type="button"
                    disabled={submittingId === v.id || !managerOk}
                    onClick={() => onAck(v.id, who, notes[v.id] || "")}
                    className="shrink-0 rounded-lg bg-amber-500 px-4 py-2 text-sm font-bold text-white shadow-sm transition hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    {submittingId === v.id ? "처리 중" : "확인"}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* 푸터 */}
        <div className="flex items-center justify-between gap-3 border-t border-gray-100 px-6 py-4 dark:border-white/10">
          <div className="text-xs text-gray-400">
            {!managerOk && list.length > 0
              ? "관리자 이름을 입력하면 확인할 수 있습니다."
              : " "}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl bg-gray-100 px-5 py-2.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-200 dark:bg-white/10 dark:text-gray-200 dark:hover:bg-white/15"
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  );
}
